/**
 * NATURAH THEME - Cart Drawer
 * Slide-out cart that confirms add-to-cart in context: free-shipping progress,
 * ajax line-item steppers, a "Complete Your Set" cross-sell, and a Checkout CTA.
 * File: assets/cart-drawer.js
 *
 * Opens on the `cart:item-added` event (dispatched by quick-add.js) and on a click
 * of the header cart icon ([data-cart-drawer-toggle]). Progressive enhancement:
 * with JS off, the header cart link still navigates to /cart.
 *
 * Exposes window.NaturahCartUI (formatMoney + shippingState) so the cart page
 * (sections/cart.liquid) shares one source of truth for money formatting and the
 * free-shipping threshold math — both read the SHIPPABLE value AFTER discounts
 * (shippingBasis), because checkout's free Ground Advantage rate checks the discounted
 * order value. Digital value cannot earn physical shipping.
 */

/* ------------------------------------------------------------------ *
 * Shared cart UI helpers (money formatting + free-ship threshold math)
 * ------------------------------------------------------------------ */
window.NaturahCartUI = window.NaturahCartUI || (function () {
  var moneyFormat = '${{amount}}';

  function setMoneyFormat(fmt) {
    if (fmt) moneyFormat = fmt;
  }

  // Canonical Shopify money formatter (vanilla). Cents in → currency string out,
  // honouring shop.money_format so the drawer matches the cart page's Liquid output.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || moneyFormat;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = (typeof precision === 'undefined') ? 2 : precision;
      thousands = (typeof thousands === 'undefined') ? ',' : thousands;
      decimal = (typeof decimal === 'undefined') ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value;
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Shippable value after discounts, in cents (mirrors sections/cart.liquid): line
  // final_line_price excludes the cart-level discount, so spread cart.total_price over
  // items_subtotal_price pro rata. Integer floor matches Liquid's divided_by.
  function shippingBasis(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var shippable = cart.items.reduce(function (sum, item) {
      return item.requires_shipping ? sum + (item.final_line_price || 0) : sum;
    }, 0);
    if (!cart.items_subtotal_price) return shippable;
    return Math.floor((shippable * cart.total_price) / cart.items_subtotal_price);
  }

  // Free-shipping state on the after-discount shippable basis (mirrors sections/cart.liquid):
  //   remaining rounds UP to whole dollars so "add $X" always clears the bar;
  //   progress floors (matches Liquid integer division); near >= 60%.
  function shippingState(basisCents, thresholdCents) {
    basisCents = basisCents || 0;
    thresholdCents = thresholdCents || 0;
    if (thresholdCents <= 0 || basisCents >= thresholdCents) {
      return { state: 'unlocked', remainingCents: 0, remainingDollars: 0, progressPct: 100 };
    }
    var remaining = thresholdCents - basisCents;
    var remainingDollars = Math.ceil(remaining / 100);
    var progressPct = Math.floor((basisCents * 100) / thresholdCents);
    return {
      state: progressPct >= 60 ? 'near' : 'below',
      remainingCents: remaining,
      remainingDollars: remainingDollars,
      progressPct: progressPct
    };
  }

  return {
    setMoneyFormat: setMoneyFormat,
    formatMoney: formatMoney,
    shippingState: shippingState,
    shippingBasis: shippingBasis
  };
})();

/* ------------------------------------------------------------------ *
 * Cart drawer controller
 * ------------------------------------------------------------------ */
class CartDrawer {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('[data-cart-drawer-panel]');
    this.backdrop = root.querySelector('[data-cart-drawer-backdrop]');
    this.closeBtn = root.querySelector('[data-cart-drawer-close]');
    this.itemsEl = root.querySelector('[data-cart-drawer-items]');
    this.loadingEl = root.querySelector('[data-cart-drawer-loading]');
    this.emptyEl = root.querySelector('[data-cart-drawer-empty]');
    this.shippingEl = root.querySelector('[data-cart-drawer-shipping]');
    this.shippingTrustEl = root.querySelector('[data-cart-drawer-shipping-trust]');
    this.taxNoteEl = root.querySelector('[data-cart-drawer-tax-note]');
    this.footerEl = root.querySelector('[data-cart-drawer-footer]');
    this.subtotalEl = root.querySelector('[data-cart-drawer-subtotal]');
    this.discountEl = root.querySelector('[data-cart-drawer-discount]');
    this.discountTitleEl = root.querySelector('[data-cart-drawer-discount-title]');
    this.discountValueEl = root.querySelector('[data-cart-drawer-discount-value]');
    this.totalEl = root.querySelector('[data-cart-drawer-total]');
    this.totalValueEl = root.querySelector('[data-cart-drawer-total-value]');
    this.shippingEstEl = root.querySelector('[data-cart-drawer-shipping-est]');
    this.shippingEstValueEl = root.querySelector('[data-cart-drawer-shipping-est-value]');
    this.crossSellEl = root.querySelector('[data-cart-drawer-cross-sell]');
    this.crossSellGrid = root.querySelector('[data-cart-drawer-cross-sell-grid]');

    this.threshold = parseInt(root.dataset.shippingThreshold, 10) || 0;
    this.shippingCost = parseInt(root.dataset.shippingCost, 10) || 0;
    this.cartUrl = (root.dataset.cartUrl || '/cart').replace(/\/$/, '');
    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.routeRoot = this.routeRoot.replace(/\/$/, '');

    window.NaturahCartUI.setMoneyFormat(root.dataset.moneyFormat);

    this.isOpen = false;
    this.invoker = null;
    this.hasRendered = false;
    this.crossSellLoadedFor = null;

    this.init();
  }

  init() {
    if (!this.panel) return;

    // Open triggers: header cart icon (and any other [data-cart-drawer-toggle]).
    // On the cart page itself the drawer would only duplicate the page underneath it (#160):
    // let the icon's href navigate instead.
    const onCartPage = window.location.pathname.replace(/\/$/, '') === this.cartUrl;
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (onCartPage) return;
        e.preventDefault();
        this.open(el);
      });
    });

    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());

    // Open + refresh whenever another add-to-cart path fires the event.
    document.addEventListener('cart:item-added', () => {
      // Attribution is best-effort; cart UI must not wait for its network request.
      void window.NaturahAttribution?.syncCart();
      this.open();
    });

    // Global key handling: Esc closes; Tab is trapped inside the panel.
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    });

    // Delegated line-item + cross-sell actions (survive re-renders).
    this.panel.addEventListener('click', (e) => this.handleClick(e));
    this.itemsEl?.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-drawer-qty-input]');
      if (input) this.changeQuantity(input.dataset.key, parseInt(input.value, 10));
    });
  }

  /* -------------------------- open / close -------------------------- */

  open(invoker) {
    if (invoker) this.invoker = invoker;
    else if (document.activeElement && document.activeElement !== document.body) {
      this.invoker = document.activeElement;
    }

    this.isOpen = true;
    this.root.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    this.panel.setAttribute('aria-hidden', 'false');
    this.backdrop?.setAttribute('aria-hidden', 'false');

    // Move focus into the dialog (#197). The panel flips to visibility:visible with
    // .is-open, but its children run their own `transition: all`, so the close button
    // stays visibility:hidden (unfocusable) for the length of that transition. Land on
    // the panel itself first (tabindex="-1", visible at once), then move to the close
    // button when it can take focus.
    this.focusIntoPanel();
    this.focusCloseWhenReady();

    this.render();
  }

  focusIntoPanel() {
    if (!this.isOpen || this.panel.contains(document.activeElement)) return;
    if (this.closeBtn) this.closeBtn.focus();
    if (!this.panel.contains(document.activeElement)) this.panel.focus();
  }

  focusCloseWhenReady() {
    if (!this.closeBtn) return;
    const tryClose = () => {
      if (!this.isOpen) return;
      const active = document.activeElement;
      // Only promote focus from the panel container itself; never steal it from a control.
      if (active === this.panel || !this.panel.contains(active)) this.closeBtn.focus();
    };
    requestAnimationFrame(() => requestAnimationFrame(tryClose));
    const onEnd = () => { this.panel.removeEventListener('transitionend', onEnd); tryClose(); };
    this.panel.addEventListener('transitionend', onEnd);
  }

  close() {
    this.isOpen = false;
    this.root.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.backdrop?.setAttribute('aria-hidden', 'true');

    // Return focus to whatever opened the drawer.
    if (this.invoker && typeof this.invoker.focus === 'function') {
      this.invoker.focus();
    }
    this.invoker = null;
  }

  trapFocus(e) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const visible = Array.prototype.filter.call(focusable, (el) => el.offsetParent !== null || el === document.activeElement);
    if (!visible.length) {
      e.preventDefault();
      this.panel.focus();
      return;
    }
    const first = visible[0];
    const last = visible[visible.length - 1];
    // Focus outside the dialog (initial focus() lost, backdrop click) or parked on the panel
    // container itself: Tab must land on a control inside, never walk the page behind the
    // drawer. If the control is still unfocusable (mid-transition), hold the panel.
    if (!this.panel.contains(document.activeElement) || document.activeElement === this.panel) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      if (!this.panel.contains(document.activeElement)) this.panel.focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------- rendering --------------------------- */

  setLoading(on) {
    if (this.loadingEl) this.loadingEl.hidden = !on;
  }

  async render() {
    this.setLoading(!this.hasRendered);
    try {
      const res = await fetch(`${this.routeRoot}/cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart drawer: failed to load cart', err);
    } finally {
      this.setLoading(false);
      this.hasRendered = true;
    }
  }

  renderCart(cart) {
    const isEmpty = !cart || cart.item_count === 0;

    if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
    if (this.itemsEl) this.itemsEl.hidden = isEmpty;
    if (this.footerEl) this.footerEl.hidden = isEmpty;
    this.renderShipping(cart);
    if (this.crossSellEl && isEmpty) this.crossSellEl.hidden = true;

    if (isEmpty) {
      if (this.itemsEl) this.itemsEl.innerHTML = '';
      return;
    }

    // Line items
    this.itemsEl.innerHTML = '';
    cart.items.forEach((item, index) => {
      this.itemsEl.appendChild(this.buildLineItem(item, index + 1));
    });

    // Subtotal — merchandise value before discount when a discount applies,
    // matching the cart page's headline figure.
    const subtotalCents = cart.original_total_price > cart.total_price
      ? cart.original_total_price
      : cart.total_price;
    if (this.subtotalEl) this.subtotalEl.textContent = window.NaturahCartUI.formatMoney(subtotalCents);
    this.renderTotals(cart);

    this.renderCrossSell(cart);
  }

  // Discount / Shipping / Estimated Total rows, mirroring sections/cart.liquid's breakdown.
  // The sitewide automatic discount is cart-level (cart.total_discount /
  // cart_level_discount_applications), so line prices stay at list price; without these
  // rows the drawer contradicted the after-discount price on the PDP and the cart page by
  // showing only the pre-discount subtotal, and its total omitted the shipping charge the cart page adds below
  // the free-shipping threshold. Shipping row: FREE at/above the after-discount threshold,
  // the standard charge below it, hidden for a digital-only cart (nothing ships).
  renderTotals(cart) {
    const fm = window.NaturahCartUI.formatMoney;
    const discountCents = cart && cart.total_discount ? cart.total_discount : 0;
    const showDiscount = discountCents > 0;
    if (this.discountEl) this.discountEl.hidden = !showDiscount;
    if (showDiscount) {
      const apps = Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
      const title = apps.map(a => a && a.title).filter(Boolean).join(', ');
      if (this.discountTitleEl) this.discountTitleEl.textContent = title ? `(${title})` : '';
      if (this.discountValueEl) this.discountValueEl.textContent = `-${fm(discountCents)}`;
    }

    const shippableItems = cart ? cart.items.filter(item => item.requires_shipping) : [];
    const requiresShipping = shippableItems.length > 0;
    let shippingEstimate = 0;
    if (this.shippingEstEl) this.shippingEstEl.hidden = !requiresShipping;
    if (requiresShipping) {
      const s = window.NaturahCartUI.shippingState(window.NaturahCartUI.shippingBasis(cart), this.threshold);
      shippingEstimate = s.state === 'unlocked' ? 0 : this.shippingCost;
      if (this.shippingEstValueEl) {
        this.shippingEstValueEl.textContent = shippingEstimate === 0 ? 'FREE' : fm(shippingEstimate);
        this.shippingEstValueEl.classList.toggle('cart-drawer__shipping-est-value--free', shippingEstimate === 0);
      }
    }

    if (this.totalEl) this.totalEl.hidden = false;
    if (this.totalValueEl) this.totalValueEl.textContent = fm((cart.total_price || 0) + shippingEstimate);
  }

  buildLineItem(item, line) {
    const li = document.createElement('li');
    li.className = 'cart-drawer__item';
    li.dataset.key = item.key;
    li.setAttribute('data-testid', 'cart-drawer-item');

    const imgUrl = item.image ? this.sizedImage(item.image, 160) : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<span class="cart-drawer__item-variant">${this.escape(item.variant_title)}</span>`
      : '';

    li.innerHTML = `
      <a class="cart-drawer__item-image" href="${item.url}" tabindex="-1" aria-hidden="true">
        ${imgUrl ? `<img src="${imgUrl}" alt="" width="80" height="80" loading="lazy">` : ''}
      </a>
      <div class="cart-drawer__item-details">
        <a class="cart-drawer__item-title" href="${item.url}">${this.escape(item.product_title)}</a>
        ${variant}
        <span class="cart-drawer__item-price">${window.NaturahCartUI.formatMoney(item.final_price)}</span>
        <div class="cart-drawer__item-controls">
          <div class="cart-drawer__qty" role="group" aria-label="Quantity for ${this.escape(item.product_title)}">
            <button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-decrease data-key="${item.key}" aria-label="Decrease quantity">−</button>
            <input type="number" class="cart-drawer__qty-input" data-cart-drawer-qty-input data-key="${item.key}"
                   value="${item.quantity}" min="0" inputmode="numeric" aria-label="Quantity for ${this.escape(item.product_title)}">
            <button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-increase data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="cart-drawer__item-remove" data-cart-drawer-remove data-key="${item.key}"
                  aria-label="Remove ${this.escape(item.product_title)} from cart">Remove</button>
        </div>
      </div>
      <span class="cart-drawer__item-line-price">${window.NaturahCartUI.formatMoney(item.final_line_price)}</span>
    `;
    return li;
  }

  renderShipping(cart) {
    const shippableItems = cart ? cart.items.filter(item => item.requires_shipping) : [];
    const requiresShipping = shippableItems.length > 0;
    if (this.shippingTrustEl) this.shippingTrustEl.hidden = !requiresShipping;
    if (this.taxNoteEl) this.taxNoteEl.textContent = requiresShipping
      ? 'Shipping & taxes calculated at checkout'
      : 'Taxes calculated at checkout';
    if (!this.shippingEl) return;
    this.shippingEl.hidden = !requiresShipping;
    if (!requiresShipping) {
      this.shippingEl.innerHTML = '';
      delete this.shippingEl.dataset.shippingState;
      return;
    }
    // After-discount shippable value. Match the Liquid cart page.
    const s = window.NaturahCartUI.shippingState(window.NaturahCartUI.shippingBasis(cart), this.threshold);
    this.shippingEl.dataset.shippingState = s.state;

    if (s.state === 'unlocked') {
      this.shippingEl.innerHTML = `
        <div class="cart-drawer__shipping-row cart-drawer__shipping-row--unlocked">
          <span class="cart-drawer__shipping-icon" aria-hidden="true">🎉</span>
          <span class="cart-drawer__shipping-text">You've unlocked <strong>free shipping</strong></span>
        </div>`;
    } else {
      const msg = s.state === 'near'
        ? `You're only <strong>$${s.remainingDollars}</strong> away from free shipping`
        : `Add <strong>$${s.remainingDollars}</strong> more for free shipping`;
      this.shippingEl.innerHTML = `
        <div class="cart-drawer__shipping-row">
          <span class="cart-drawer__shipping-icon" aria-hidden="true">🚚</span>
          <span class="cart-drawer__shipping-text">${msg}</span>
        </div>
        <div class="cart-drawer__shipping-bar">
          <div class="cart-drawer__shipping-fill" style="width: ${s.progressPct}%"></div>
        </div>`;
    }
  }

  async renderCrossSell(cart) {
    if (!this.crossSellEl || !this.crossSellGrid) return;
    // Manual curated picks (settings.cross_sell_products) are server-rendered into the grid
    // by snippets/cart-drawer.liquid — the ONE source shared with the cart page
    // (sections/cart.liquid). When present, honor them and skip the API entirely.
    if (this.crossSellEl.dataset.hasManualPicks) {
      this.crossSellEl.hidden = this.crossSellGrid.children.length === 0;
      return;
    }
    const seed = cart.items[0] && cart.items[0].product_id;
    if (!seed) {
      this.crossSellEl.hidden = true;
      return;
    }
    // Avoid refetching for the same seed product on every re-render.
    if (this.crossSellLoadedFor === seed && this.crossSellGrid.children.length) {
      this.crossSellEl.hidden = false;
      return;
    }

    const inCart = cart.items.map((i) => i.product_id);
    let products = await this.fetchRecommendations(seed, 'complementary');
    if (!products.length) products = await this.fetchRecommendations(seed, 'related');
    products = products.filter((p) => inCart.indexOf(p.id) === -1).slice(0, 3);

    if (!products.length) {
      this.crossSellEl.hidden = true;
      return;
    }

    this.crossSellGrid.innerHTML = '';
    products.forEach((p) => this.crossSellGrid.appendChild(this.buildCrossSellCard(p)));
    this.crossSellEl.hidden = false;
    this.crossSellLoadedFor = seed;
  }

  async fetchRecommendations(productId, intent) {
    try {
      const url = `${this.routeRoot}/recommendations/products.json?product_id=${productId}&intent=${intent}&limit=6`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.products || []).filter((p) => p.available && p.variants && p.variants.length);
    } catch (err) {
      return [];
    }
  }

  buildCrossSellCard(product) {
    const card = document.createElement('div');
    card.className = 'cart-drawer__cross-sell-card';
    card.setAttribute('data-testid', 'cart-drawer-cross-sell-card');
    const imgUrl = product.featured_image ? this.sizedImage(product.featured_image, 120) : '';
    const variantId = product.variants[0].id;

    card.innerHTML = `
      <a class="cart-drawer__cross-sell-link" href="${product.url}">
        <span class="cart-drawer__cross-sell-image">
          ${imgUrl ? `<img src="${imgUrl}" alt="" width="56" height="56" loading="lazy">` : ''}
        </span>
        <span class="cart-drawer__cross-sell-info">
          <span class="cart-drawer__cross-sell-name">${this.escape(product.title)}</span>
          <span class="cart-drawer__cross-sell-price">${window.NaturahCartUI.formatMoney(product.price)}</span>
        </span>
      </a>
      <button type="button" class="btn btn--secondary btn--small cart-drawer__cross-sell-add"
              data-cart-drawer-add data-variant-id="${variantId}"
              aria-label="Add ${this.escape(product.title)} to cart">Add</button>
    `;
    return card;
  }

  /* --------------------------- mutations ---------------------------- */

  handleClick(e) {
    const decrease = e.target.closest('[data-cart-drawer-qty-decrease]');
    const increase = e.target.closest('[data-cart-drawer-qty-increase]');
    const remove = e.target.closest('[data-cart-drawer-remove]');
    const add = e.target.closest('[data-cart-drawer-add]');

    if (decrease) {
      const input = this.qtyInput(decrease.dataset.key);
      this.changeQuantity(decrease.dataset.key, Math.max(0, parseInt(input.value, 10) - 1));
    } else if (increase) {
      const input = this.qtyInput(increase.dataset.key);
      this.changeQuantity(increase.dataset.key, parseInt(input.value, 10) + 1);
    } else if (remove) {
      this.changeQuantity(remove.dataset.key, 0);
    } else if (add) {
      this.addItem(add);
    }
  }

  qtyInput(key) {
    return this.itemsEl.querySelector(`[data-cart-drawer-qty-input][data-key="${key}"]`);
  }

  async changeQuantity(key, quantity) {
    if (isNaN(quantity) || quantity < 0) return;
    this.panel.classList.add('is-updating');
    try {
      const res = await fetch(`${this.routeRoot}/cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
      window.NaturahCart && window.NaturahCart.reconcile && window.NaturahCart.reconcile();
    } catch (err) {
      console.error('Cart drawer: failed to update quantity', err);
    } finally {
      this.panel.classList.remove('is-updating');
    }
  }

  async addItem(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    button.disabled = true;
    button.classList.add('is-adding');
    const label = button.textContent;
    button.textContent = 'Adding…';
    try {
      const response = await fetch(`${this.routeRoot}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (response.ok) void window.NaturahAttribution?.syncCart();
      await this.render();
      window.NaturahCart && window.NaturahCart.reconcile && window.NaturahCart.reconcile();
    } catch (err) {
      console.error('Cart drawer: failed to add item', err);
      button.disabled = false;
      button.classList.remove('is-adding');
      button.textContent = label;
    }
  }

  /* ----------------------------- utils ------------------------------ */

  sizedImage(url, width) {
    if (!url) return '';
    // Insert Shopify CDN size (e.g. _160x) before the file extension, ahead of any ?v= query.
    return url.replace(/(\.(?:png|jpg|jpeg|gif|webp))(\?|$)/i, `_${width}x$1$2`);
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : str;
    return div.innerHTML;
  }
}

/* ------------------------------- init ------------------------------ */
function initCartDrawer() {
  const root = document.querySelector('[data-cart-drawer]');
  if (root && !root.dataset.drawerInit) {
    root.dataset.drawerInit = 'true';
    window.naturahCartDrawer = new CartDrawer(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawer);
} else {
  initCartDrawer();
}
